/**
 * @fileoverview Validation for embedded sub-navigation item payloads.
 */

import type { ValidationResult } from "./types";

const MAX_SUB_NAV_CHILDREN = 50;

function fieldPath(prefix: string, key: string): string {
  if (!prefix) return key;
  return `${prefix}.${key}`;
}

function validateNavAddFields(
  row: Record<string, unknown>,
  pathPrefix: string,
): string[] {
  const errors: string[] = [];

  (["title", "value", "url"] as const).forEach((key) => {
    if (typeof row[key] !== "string" || !String(row[key]).trim()) {
      errors.push(
        `Sub-nav addItem "${fieldPath(pathPrefix, key)}" must be non-empty string`,
      );
    }
  });

  const disabledKey = fieldPath(pathPrefix, "disabled");
  const activeKey = fieldPath(pathPrefix, "active");

  if (row.disabled !== undefined && typeof row.disabled !== "boolean") {
    errors.push(`Sub-nav addItem "${disabledKey}" must be boolean`);
  }
  if (row.active !== undefined && typeof row.active !== "boolean") {
    errors.push(`Sub-nav addItem "${activeKey}" must be boolean`);
  }

  return errors;
}

/**
 * Validate one node (root or child). When `allowNestedChildren` is false,
 * `children` must be absent or an empty array.
 */
function validateNavAddNode(
  raw: unknown,
  pathPrefix: string,
  allowNestedChildren: boolean,
): string[] {
  if (typeof raw !== "object" || raw === null) {
    const label = pathPrefix || "item";
    return [`Sub-nav addItem "${label}" must be an object`];
  }
  const row = raw as Record<string, unknown>;
  const errors = validateNavAddFields(row, pathPrefix);

  const childrenKey = fieldPath(pathPrefix, "children");

  if (row.children === undefined) {
    return errors;
  }

  if (!allowNestedChildren) {
    if (Array.isArray(row.children) && row.children.length === 0) {
      return errors;
    }
    errors.push(
      `Sub-nav addItem "${childrenKey}" is not supported on submenu rows (only one dropdown level)`,
    );
    return errors;
  }

  if (!Array.isArray(row.children)) {
    errors.push(`Sub-nav addItem "${childrenKey}" must be an array`);
    return errors;
  }

  if (row.children.length > MAX_SUB_NAV_CHILDREN) {
    errors.push(
      `Sub-nav addItem "${childrenKey}" must have at most ${MAX_SUB_NAV_CHILDREN} items`,
    );
    return errors;
  }

  row.children.forEach((child, index) => {
    const childPath = pathPrefix
      ? `${pathPrefix}.children[${index}]`
      : `children[${index}]`;
    errors.push(...validateNavAddNode(child, childPath, false));
  });

  return errors;
}

/**
 * Validate payload for embedded::nav.addItem
 */
function collectNavAddValues(
  raw: unknown,
  pathPrefix: string,
  allowNestedChildren: boolean,
  out: string[],
): string[] {
  if (typeof raw !== "object" || raw === null) {
    return out;
  }
  const row = raw as Record<string, unknown>;
  if (typeof row.value === "string" && row.value.trim()) {
    out.push(row.value.trim());
  }
  if (!allowNestedChildren || !Array.isArray(row.children)) {
    return out;
  }
  row.children.forEach((child, index) => {
    const childPath = pathPrefix
      ? `${pathPrefix}.children[${index}]`
      : `children[${index}]`;
    collectNavAddValues(child, childPath, false, out);
  });
  return out;
}

function validateNavAddValuesUnique(item: unknown): string[] {
  const values = collectNavAddValues(item, "", true, []);
  const seen = new Set<string>();
  const dupes: string[] = [];
  for (const v of values) {
    if (seen.has(v)) {
      dupes.push(v);
    }
    seen.add(v);
  }
  if (dupes.length === 0) return [];
  return [
    `Sub-nav addItem "value" must be globally unique within the item tree (duplicate: ${[...new Set(dupes)].join(", ")})`,
  ];
}

/**
 * Validate payload for embedded::nav.addItem
 */
export function validateEmbeddedNavAddItem(item: unknown): ValidationResult {
  if (typeof item !== "object" || item === null) {
    return {
      valid: false,
      errors: ["Nav addItem expects an object item"],
    };
  }

  const errors = [
    ...validateNavAddNode(item, "", true),
    ...validateNavAddValuesUnique(item),
  ];
  return { valid: errors.length === 0, errors };
}

/**
 * Validate payload for embedded::nav.updateItem.
 */
export function validateEmbeddedNavPatchItem(item: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof item !== "object" || item === null) {
    errors.push("Nav updateItem expects an object item");
    return { valid: false, errors };
  }
  const row = item as Record<string, unknown>;

  const hasValueId =
    typeof row.value === "string" && String(row.value).trim().length > 0;
  const hasLegacyId =
    typeof row.id === "string" && String(row.id).trim().length > 0;
  const trimmedValue = hasValueId ? String(row.value).trim() : "";
  const trimmedId = hasLegacyId ? String(row.id).trim() : "";
  if (!hasValueId && !hasLegacyId) {
    errors.push(`Sub-nav updateItem requires non-empty string "value"`);
    return { valid: false, errors };
  }
  if (hasValueId && hasLegacyId && trimmedValue !== trimmedId) {
    errors.push(
      `Sub-nav updateItem must not specify different "id" and "value"; "value" is the immutable row key and cannot be renamed via patch`,
    );
    return { valid: false, errors };
  }
  /** Row key used for duplicate checks when `children` replaces a parent's list. */
  const parentRowKey = trimmedValue || trimmedId;

  let fieldCount = 0;
  if (row.title !== undefined) fieldCount++;
  if (row.url !== undefined) fieldCount++;
  if (row.disabled !== undefined) fieldCount++;
  if (row.active !== undefined) fieldCount++;
  if (row.children !== undefined) fieldCount++;
  if (fieldCount === 0) {
    errors.push(
      `Sub-nav updateItem must include at least one of title, url, disabled, active, children`,
    );
    return { valid: false, errors };
  }
  ["title", "url"].forEach((key) => {
    if (
      row[key] !== undefined &&
      (typeof row[key] !== "string" || !(row[key] as string).trim())
    ) {
      errors.push(`Sub-nav updateItem "${key}" must be non-empty`);
    }
  });
  if (row.disabled !== undefined && typeof row.disabled !== "boolean") {
    errors.push(`Sub-nav updateItem "disabled" must be boolean`);
  }
  if (row.active !== undefined && typeof row.active !== "boolean") {
    errors.push(`Sub-nav updateItem "active" must be boolean`);
  }

  if (row.children !== undefined) {
    if (!Array.isArray(row.children)) {
      errors.push(`Sub-nav updateItem "children" must be an array`);
    } else {
      if (row.children.length > MAX_SUB_NAV_CHILDREN) {
        errors.push(
          `Sub-nav updateItem "children" must have at most ${MAX_SUB_NAV_CHILDREN} items`,
        );
      } else {
        const childValues = new Set<string>();
        row.children.forEach((child, index) => {
          const childPath = `children[${index}]`;
          errors.push(...validateNavAddNode(child, childPath, false));
          if (typeof child === "object" && child !== null) {
            const cv = (child as Record<string, unknown>).value;
            if (typeof cv === "string" && cv.trim()) {
              const t = cv.trim();
              if (t === parentRowKey) {
                errors.push(
                  `Sub-nav updateItem "${childPath}.value" must not equal the parent row identifier`,
                );
              }
              if (childValues.has(t)) {
                errors.push(
                  `Sub-nav updateItem "${childPath}.value" must be unique within children`,
                );
              }
              childValues.add(t);
            }
          }
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate embedded::nav.removeItem value payload.
 */
export function validateEmbeddedNavRemoveValue(
  value: unknown,
): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "string" || !value.trim()) {
    errors.push(`Nav removeItem value must be non-empty string`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * @deprecated Use {@link validateEmbeddedNavRemoveValue} instead.
 */
export function validateEmbeddedNavRemoveId(id: unknown): ValidationResult {
  return validateEmbeddedNavRemoveValue(id);
}
